import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GetDoctorList,
  GetDoctorDetail,
  CreateDoctor,
  UpdateDoctor,
  DeleteDoctor,
} from "../../Services/DoctorService";

/**
 * Doctor State Interface with caching
 */
export interface DoctorState {
  // List data
  doctorCollection: any[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;

  // Detail data
  selectedDoctorDetail: any | null;

  // Loading states
  loadingList: boolean;
  loadingDetail: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;

  // Error states
  error: string | null;
  errorDetail: string | null;

  // Metadata
  sortColumn: string;
  sortDirection: string;
  searchValue: string;

  // Cache management
  cache: Map<string, CacheEntry>;
  lastFetchParams: FetchParams | null;

  // Success flags for UX
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number;
}

interface FetchParams {
  consumerId: number;
  firmId: number;
  branchId: number;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Generate cache key based on fetch parameters
 */
const generateCacheKey = (params: FetchParams): string => {
  return `doctor_list_${params.consumerId}_${params.firmId}_${params.branchId}_${params.pageIndex}_${params.searchValue}`;
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (entry: CacheEntry): boolean => {
  return Date.now() - entry.timestamp < entry.expiresIn;
};

/**
 * Async Thunk: Fetch doctor list
 */
export const fetchDoctorList = createAsyncThunk(
  "doctor/fetchDoctorList",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
      pageIndex: number;
      pageSize: number;
      sortColumn: string;
      sortDirection: string;
      searchValue: string;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const root = getState() as { doctor: DoctorState };
      const cacheKey = generateCacheKey(params);

      // Check if we have valid cache
      if (root.doctor.cache.has(cacheKey)) {
        const cached = root.doctor.cache.get(cacheKey)!;
        if (isCacheValid(cached)) {
          return cached.data;
        }
      }

      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("pageIndex", params.pageIndex.toString());
      urlParams.append("pageSize", params.pageSize.toString());
      urlParams.append("sortColumn", params.sortColumn);
      urlParams.append("sortDirection", params.sortDirection);
      urlParams.append("searchValue", params.searchValue);

      const result: any = await GetDoctorList(urlParams);

      if (result?.data?.data?.doctorCollection) {
        return result.data.data;
      }

      return rejectWithValue("No doctor data received");
    } catch (error: any) {
      console.error("Error fetching doctor list:", error);
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch doctors"
      );
    }
  }
);

/**
 * Async Thunk: Fetch single doctor detail
 */
export const fetchDoctorDetail = createAsyncThunk(
  "doctor/fetchDoctorDetail",
  async (
    params: {
      consumerID: number;
      firmId: number;
      branchId: number;
      doctorID: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append("consumerID", params.consumerID.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("doctorID", params.doctorID.toString());

      const result: any = await GetDoctorDetail(urlParams);

      if (result?.data?.data) {
        return result.data.data;
      }

      return rejectWithValue("No doctor detail received");
    } catch (error: any) {
      console.error("Error fetching doctor detail:", error);
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch doctor details"
      );
    }
  }
);

/**
 * Async Thunk: Create new doctor
 */
export const createDoctorAsync = createAsyncThunk(
  "doctor/createDoctor",
  async (doctorData: any, { rejectWithValue }) => {
    try {
      const result: any = await CreateDoctor(doctorData);

      if (result?.data) {
        return result.data;
      }

      return rejectWithValue("Failed to create doctor");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to create doctor"
      );
    }
  }
);

/**
 * Async Thunk: Update doctor
 */
export const updateDoctorAsync = createAsyncThunk(
  "doctor/updateDoctor",
  async (doctorData: any, { rejectWithValue }) => {
    try {
      const result: any = await UpdateDoctor(doctorData);

      if (result?.data) {
        return result.data;
      }

      return rejectWithValue("Failed to update doctor");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to update doctor"
      );
    }
  }
);

/**
 * Async Thunk: Delete doctor
 */
export const deleteDoctorAsync = createAsyncThunk(
  "doctor/deleteDoctor",
  async (deleteData: any, { rejectWithValue }) => {
    try {
      const result: any = await DeleteDoctor(deleteData);
      if (result?.data || result?.Data) {
        return deleteData;
      }

      return rejectWithValue("Failed to delete doctor");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to delete doctor"
      );
    }
  }
);

const initialState: DoctorState = {
  doctorCollection: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: 10,
  selectedDoctorDetail: null,
  loadingList: false,
  loadingDetail: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,
  error: null,
  errorDetail: null,
  sortColumn: "",
  sortDirection: "asc",
  searchValue: "",
  cache: new Map(),
  lastFetchParams: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

const doctorSlice = createSlice({
  name: "doctor",
  initialState,
  reducers: {
    /**
     * Clear all doctor data
     */
    clearDoctorData: (state) => {
      state.doctorCollection = [];
      state.totalCount = 0;
      state.selectedDoctorDetail = null;
      state.error = null;
    },

    /**
     * Clear doctor detail
     */
    clearDoctorDetail: (state) => {
      state.selectedDoctorDetail = null;
      state.errorDetail = null;
    },

    /**
     * Clear errors
     */
    clearErrors: (state) => {
      state.error = null;
      state.errorDetail = null;
    },

    /**
     * Set doctor collection manually
     */
    setDoctorCollection: (state, action: PayloadAction<any[]>) => {
      state.doctorCollection = action.payload;
    },

    /**
     * Clear success flags
     */
    clearSuccessFlags: (state) => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },

    /**
     * Invalidate cache for refetch
     */
    invalidateCache: (state) => {
      state.cache.clear();
    },

    /**
     * Set fetch metadata
     */
    setFetchMetadata: (
      state,
      action: PayloadAction<Partial<FetchParams>>
    ) => {
      state.searchValue = action.payload.searchValue || state.searchValue;
      state.sortColumn = action.payload.sortColumn || state.sortColumn;
      state.sortDirection = action.payload.sortDirection || state.sortDirection;
      state.pageIndex = action.payload.pageIndex || state.pageIndex;
    },
  },
  extraReducers: (builder) => {
    /**
     * Fetch Doctor List handlers
     */
    builder
      .addCase(fetchDoctorList.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(
        fetchDoctorList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingList = false;
          state.doctorCollection = action.payload?.doctorCollection || [];
          state.totalCount = action.payload?.totalCount || 0;
          state.pageIndex = action.payload?.pageIndex || 1;
          state.pageSize = action.payload?.pageSize || 10;

          // Update cache
          if (state.lastFetchParams) {
            const cacheKey = generateCacheKey(state.lastFetchParams);
            state.cache.set(cacheKey, {
              data: action.payload,
              timestamp: Date.now(),
              expiresIn: CACHE_DURATION,
            });
          }
        }
      )
      .addCase(fetchDoctorList.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    /**
     * Fetch Doctor Detail handlers
     */
    builder
      .addCase(fetchDoctorDetail.pending, (state) => {
        state.loadingDetail = true;
        state.errorDetail = null;
      })
      .addCase(
        fetchDoctorDetail.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingDetail = false;
          state.selectedDoctorDetail = action.payload;
        }
      )
      .addCase(fetchDoctorDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.errorDetail = action.payload as string;
      });

    /**
     * Create Doctor handlers
     */
    builder
      .addCase(createDoctorAsync.pending, (state) => {
        state.loadingCreate = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createDoctorAsync.fulfilled, (state, action) => {
        state.loadingCreate = false;
        state.createSuccess = true;
        state.cache.clear();
      })
      .addCase(createDoctorAsync.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload as string;
      });

    /**
     * Update Doctor handlers
     */
    builder
      .addCase(updateDoctorAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateDoctorAsync.fulfilled, (state, action) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
        state.cache.clear();
      })
      .addCase(updateDoctorAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });

    /**
     * Delete Doctor handlers
     */
    builder
      .addCase(deleteDoctorAsync.pending, (state) => {
        state.loadingDelete = true;
        state.error = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteDoctorAsync.fulfilled, (state, action) => {
        state.loadingDelete = false;
        state.deleteSuccess = true;
        state.cache.clear();
      })
      .addCase(deleteDoctorAsync.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearDoctorData,
  clearDoctorDetail,
  clearErrors,
  setDoctorCollection,
  clearSuccessFlags,
  invalidateCache,
  setFetchMetadata,
} = doctorSlice.actions;

export default doctorSlice.reducer;
